import React, { useState, useEffect } from 'react';
import { Shield, Plus, Users, Check } from 'lucide-react';
import { Community, User } from '../../types';
import { api } from '../../services/api';
import styles from './Communities.module.css';

interface CommunityViewProps {
  currentUser: User | null;
}

export const CommunityView: React.FC<CommunityViewProps> = ({ currentUser }) => {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCommunities();
  }, []);

  const loadCommunities = async () => {
    try {
      const res = await api.getCommunities();
      setCommunities(res.communities);
    } catch {
      // Demo communities fallback
      setCommunities([
        {
          id: 'comm_1',
          name: 'Cloudflare Architects',
          slug: 'cloudflare-architects',
          description: 'A global community of developers building serverless applications on Cloudflare Workers, D1, R2, and Durable Objects.',
          avatar_url: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400&auto=format&fit=crop&q=80',
          banner_url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&auto=format&fit=crop&q=80',
          rules: ['Be respectful', 'No spam'],
          creator_id: 'usr_cordle_official',
          is_private: false,
          members_count: 1420,
          is_joined: true,
          created_at: Date.now(),
        },
        {
          id: 'comm_2',
          name: 'UI/UX Glassmorphism Lab',
          slug: 'ui-ux-glassmorphism',
          description: 'Exploring hyper-modern CSS design systems, dark modes, animations, and micro-interactions.',
          avatar_url: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?w=400&auto=format&fit=crop&q=80',
          banner_url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&auto=format&fit=crop&q=80',
          rules: ['Share your design work'],
          creator_id: 'usr_sarah_design',
          is_private: false,
          members_count: 890,
          is_joined: false,
          created_at: Date.now(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (id: string) => {
    if (!currentUser) return alert('Please sign in to join communities');
    try {
      const res = await api.toggleJoinCommunity(id);
      setCommunities((prev) =>
        prev.map((c) => (c.id === id ? { ...c, is_joined: res.isJoined, members_count: res.membersCount } : c))
      );
    } catch (err: any) {
      alert(err.message || 'Join failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Explore Communities</h2>
        <button
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            background: 'var(--accent-gradient)',
            color: '#ffffff',
            fontWeight: '600',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          onClick={() => alert('Community creation modal ready!')}
        >
          <Plus size={16} /> Create Community
        </button>
      </div>

      <div className={styles.grid}>
        {communities.map((comm) => (
          <div key={comm.id} className={styles.card}>
            <div className={styles.banner} style={{ backgroundImage: `url(${comm.banner_url})` }} />
            <div className={styles.content}>
              <img src={comm.avatar_url} alt={comm.name} className={styles.avatar} />
              <div className={styles.title}>{comm.name}</div>
              <div className={styles.members}>
                <Users size={12} /> {comm.members_count} members
              </div>
              <div className={styles.description}>{comm.description}</div>
              <button
                className={styles.joinBtn}
                style={{ background: comm.is_joined ? 'var(--bg-tertiary)' : undefined }}
                onClick={() => handleJoin(comm.id)}
              >
                {comm.is_joined ? 'Joined ✓' : 'Join Community'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
